import { ArrowUp, ArrowDown, type LucideIcon, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * KPI Card — handoff EXAMPLES §2 + COMPONENTS §12
 * Stat atom: label eyebrow + valor mono tabular + delta opcional
 * Tones: default | mint | ink | clay | lilac
 *
 * Regra: numa tela use 2 default + 1 mint + 1 ink (CLAYMORPHISM §4)
 */
export type KPITone = 'default' | 'mint' | 'ink' | 'clay' | 'lilac';

interface KPIProps {
  label: string;
  value: string;
  delta?: string;
  deltaDir?: 'up' | 'down';
  icon?: LucideIcon;
  tone?: KPITone;
  className?: string;
}

const surfaceClasses: Record<KPITone, string> = {
  default: 'bg-card border-card-stroke shadow-1 text-fg',
  mint:
    'border-white/45 shadow-clay-mint text-on-mint ' +
    'bg-[radial-gradient(110%_90%_at_0%_0%,rgba(255,255,255,0.5),transparent_60%),linear-gradient(160deg,#D2E8DA_0%,var(--mint)_70%)]',
  ink:
    'border-white/5 shadow-clay-ink text-on-ink ' +
    'bg-[linear-gradient(160deg,var(--ink-soft)_0%,var(--ink-deep)_100%)]',
  clay:
    'border-white/45 shadow-clay-clay text-on-clay ' +
    'bg-[radial-gradient(110%_90%_at_0%_0%,rgba(255,255,255,0.55),transparent_60%),linear-gradient(160deg,#F5DCC4_0%,var(--clay)_70%)]',
  lilac:
    'border-white/45 shadow-clay text-[#2A2240] ' +
    'bg-[radial-gradient(110%_90%_at_0%_0%,rgba(255,255,255,0.5),transparent_60%),linear-gradient(160deg,#E1D7F0_0%,var(--lilac)_70%)]',
};

const iconBgClasses: Record<KPITone, string> = {
  default: 'bg-card-soft text-fg-muted',
  mint: 'bg-black/10 text-on-mint',
  ink: 'bg-white/10 text-mint-deep',
  clay: 'bg-black/10 text-on-clay',
  lilac: 'bg-black/10 text-[#2A2240]',
};

export function KPI({
  label,
  value,
  delta,
  deltaDir,
  icon: Icon = Wallet,
  tone = 'default',
  className,
}: KPIProps) {
  const labelColor = tone === 'default' ? 'text-fg-muted' : 'opacity-85';
  const deltaColor =
    tone !== 'default'
      ? 'opacity-85'
      : deltaDir === 'up'
        ? 'text-pass'
        : deltaDir === 'down'
          ? 'text-block'
          : 'text-fg-muted';

  return (
    <div
      className={cn(
        'rounded-3 p-4 border flex flex-col gap-2 min-w-0 relative overflow-hidden',
        surfaceClasses[tone],
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2.5 text-[11px] uppercase tracking-wider font-semibold',
          labelColor,
        )}
      >
        <span
          className={cn(
            'h-[26px] w-[26px] rounded-full grid place-items-center shrink-0',
            iconBgClasses[tone],
          )}
        >
          <Icon size={14} strokeWidth={1.8} />
        </span>
        <span className="truncate">{label}</span>
      </div>
      <div className="font-mono text-[28px] leading-none font-bold tracking-tight tabular-nums">
        {value}
      </div>
      {delta && (
        <div className={cn('text-[11px] font-mono flex items-center gap-1', deltaColor)}>
          {deltaDir === 'up' && <ArrowUp size={11} strokeWidth={2} />}
          {deltaDir === 'down' && <ArrowDown size={11} strokeWidth={2} />}
          <span>{delta}</span>
        </div>
      )}
    </div>
  );
}
